import {
  Box,
  Divider,
  FormControlLabel,
  Grid,
  Radio,
  RadioGroup,
  Switch
} from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PrimaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { Header4 } from 'src/components/shared/Typography/Header4';
import { MixtureTypeDetailDto } from 'src/store/mixturetypes/mixturetype';
import {
  maxLength,
  required,
  runValidations,
  validationRunner
} from 'src/utils/validation';

type Props = {
  mixtureType: Nullable<MixtureTypeDetailDto>;
  onCancel: () => void;
  onSubmit: (mixtureType: MixtureTypeDetailDto) => void;
};

export const MixtureTypeDetailsForm: React.FC<Props> = props => {
  const { onSubmit, onCancel } = props;

  const [mixtureType, setMixtureType] = useState<MixtureTypeDetailDto>(
    props.mixtureType ||
      ({
        candidateAmount: false,
        candidateSameGrade: false,
        candidateSameSpec: false,
        candidateLot: false,
        candidateSpec: false,
        candidateSpecCustomer: false,
        completeCount: false,
        completeAmount: false,
        completeSpec: false,
        completeSpecCustomer: false,
        runLotCreate: 1,
        runLotComplete: false,
        candidateCount: false
      } as MixtureTypeDetailDto)
  );

  const { t } = useTranslation();

  useEffect(() => {
    if (props.mixtureType) {
      setMixtureType(props.mixtureType);
    }
  }, [props.mixtureType]);

  const validate = (mixtureType: MixtureTypeDetailDto) => {
    const validationRunners = [
      validationRunner('name', t('props.name'), required, maxLength(50)),
      validationRunner('description', t('props.description'), maxLength(200))
    ];
    return runValidations(mixtureType, validationRunners);
  };

  return (
    <Formik
      initialValues={mixtureType}
      validate={validate}
      enableReinitialize
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values);
        setSubmitting(false);
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isSubmitting,
        submitCount
      }) => (
        <Form onSubmit={handleSubmit}>
          <Grid container>
            <Grid item md={12} lg={12} className="flex align-center jc-end">
              <TertiaryButton
                onClick={onCancel}
                label={t('cancel')}
                className="mr-32"
              />
              <PrimaryButton
                type="submit"
                label={t('save.verb')}
                disabled={isSubmitting}
              />
            </Grid>
          </Grid>

          <Divider className="my-16" />

          <InputField
            name="name"
            label={t('props.name')}
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessage={errors.name}
            displayError={touched.name || submitCount > 0}
          />
          <InputField
            name="description"
            label={t('props.description')}
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            errorMessage={errors.description}
            displayError={touched.description || submitCount > 0}
          />
          <Divider className="mb-24" />
          <Header4 className="mb-16">
            {t('mixtureType.msgRequirementCandidates')}
          </Header4>
          <Grid container>
            <Grid item sm={12} md={6}>
              <Box display="flex" flexDirection="column">
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.candidateCount}
                      onChange={handleChange}
                      name="candidateCount"
                    />
                  }
                  label={t('mixtureType.candidateCount')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.candidateAmount}
                      onChange={handleChange}
                      name="candidateAmount"
                    />
                  }
                  label={t('mixtureType.candidateAmount')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.candidateSameGrade}
                      onChange={handleChange}
                      name="candidateSameGrade"
                    />
                  }
                  label={t('mixtureType.candidateSameGrade')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.candidateSameSpec}
                      onChange={handleChange}
                      name="candidateSameSpec"
                    />
                  }
                  label={t('mixtureType.candidateSameSpec')}
                />
              </Box>
            </Grid>
            <Grid item sm={12} md={6}>
              <Box display="flex" flexDirection="column">
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.candidateLot}
                      onChange={handleChange}
                      name="candidateLot"
                    />
                  }
                  label={t('mixtureType.candidateLot')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.candidateSpec}
                      onChange={handleChange}
                      name="candidateSpec"
                    />
                  }
                  label={t('mixtureType.candidateSpec')}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.candidateSpecCustomer}
                      onChange={handleChange}
                      name="candidateSpecCustomer"
                    />
                  }
                  label={t('mixtureType.candidateSpecCustomer')}
                />
              </Box>
            </Grid>
          </Grid>
          <Divider className="my-24" />
          <Header4 className="mb-16">
            {t('mixtureType.msgRequirementComplete')}
          </Header4>
          <Box display="flex" flexDirection="column">
            <FormControlLabel
              control={
                <Switch
                  checked={values.completeCount}
                  onChange={handleChange}
                  name="completeCount"
                />
              }
              label={t('mixtureType.completeCount')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={values.completeAmount}
                  onChange={handleChange}
                  name="completeAmount"
                />
              }
              label={t('mixtureType.completeAmount')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={values.completeSpec}
                  onChange={handleChange}
                  name="completeSpec"
                />
              }
              label={t('mixtureType.completeSpec')}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={values.completeSpecCustomer}
                  onChange={handleChange}
                  name="completeSpecCustomer"
                />
              }
              label={t('mixtureType.completeSpecCustomer')}
            />
          </Box>
          <Divider className="my-24" />
          <Header4 className="mb-16">{t('mixtureType.msgCreate')}</Header4>
          <RadioGroup
            row
            name="runLotCreate"
            aria-label="label-type"
            value={values.runLotCreate}
            onChange={event => {
              setFieldValue('runLotCreate', Number(event.target.value));
            }}
          >
            <FormControlLabel
              value={1}
              control={<Radio />}
              label={t('mixtureType.runLotCreateOpt1')}
            />
            <FormControlLabel
              value={2}
              control={<Radio />}
              label={t('mixtureType.runLotCreateOpt2')}
            />
            <FormControlLabel
              value={3}
              control={<Radio />}
              label={t('mixtureType.runLotCreateOpt3')}
            />
          </RadioGroup>
          <FormControlLabel
            control={
              <Switch
                checked={values.runLotComplete}
                onChange={handleChange}
                name="runLotComplete"
              />
            }
            label={t('mixtureType.runLotComplete')}
          />
        </Form>
      )}
    </Formik>
  );
};
